package org.multiplatform.kotfolio.sections

import androidx.compose.runtime.*
import org.multiplatform.kotfolio.components.MatrixCategoryBranch
import org.multiplatform.kotfolio.components.MatrixRootNode
import org.multiplatform.kotfolio.components.sectionTitle
import org.multiplatform.kotfolio.models.Section
import org.multiplatform.kotfolio.util.Constants.SECTION_WIDTH
import org.multiplatform.kotfolio.util.observerViewPortEntered
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Text
import org.multiplatform.kotfolio.rememberAppState
import kotlin.time.Duration.Companion.milliseconds

private val AMBER_500 = Color.rgb(245, 158, 11)
private val DARK_CANVAS = Color.rgb(15, 12, 10)

data class MatrixCategory(
    val title: String,
    val skills: List<String>,
    val color: Color,
)

val matrixData = listOf(
    MatrixCategory("Core Languages", listOf("Kotlin", "Java", "SQL", "Python", "TypeScript", "JavaScript", "HTML/CSS"), AMBER_500),
    MatrixCategory(" Frameworks & Systems ", listOf("Android SDK", "Jetpack Compose", "Compose Multiplatform", "Firebase", "Git/GitHub", "Offline-First"), Color.rgb(251, 146, 60)),
    MatrixCategory("Strategic Focus", listOf("Mobile Development", "Backend Systems", "Operational Platforms", "Scalable Architecture", "Infrastructure & Hosting"), Color.rgb(251, 191, 36)),
    MatrixCategory("Web & Hybrid", listOf("Kobweb", "Varabyte", "Vue", "Svelte", "Web Development", "Testcontainers"), Color.rgb(251, 113, 133)),
    MatrixCategory("Engineering Quality", listOf("MVVM/CLEAN/MVI", "Unit Testing", "CI/CD Pipelines", "Docker/Linux", "Agile Leadership"), Color.rgb(234, 179, 8)),
)

@Composable
fun skillsSectionV2() {
    val breakpoint = rememberAppState().breakpoint
    val isDesktop = breakpoint >= Breakpoint.MD

    var animateSkills by remember { mutableStateOf(false) }

    observerViewPortEntered(
        sectionId = Section.Skills.id,
        distanceFromTop = 500.0,
        onViewPortEntered = {
            animateSkills = true
        },
    )

    // Fallback for safety in production
    LaunchedEffect(Unit) {
        delay(3000.milliseconds)
        animateSkills = true
    }

    Box(
        modifier = Modifier
            .id(Section.Skills.id)
            .fillMaxWidth()
            .maxWidth(SECTION_WIDTH.px)
            .padding(topBottom = if (isDesktop) 150.px else 80.px)
            .styleModifier {
                property("background", "linear-gradient(to bottom, #2c1910 80%, #1A0A00 100%)")
            },
        contentAlignment = Alignment.Center,
    ) {
        Column(
            modifier = Modifier.fillMaxWidth(if (isDesktop) 95.percent else 95.percent),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            sectionTitle(
                modifier = Modifier.margin(bottom = if (isDesktop) 100.px else 60.px),
                section = Section.Skills,
                alignment = Alignment.CenterHorizontally,
            )

            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .minHeight(if (isDesktop) 900.px else 0.px)
                    .backgroundColor(DARK_CANVAS)
                    .borderRadius(32.px)
                    .border(1.px, LineStyle.Solid, Color.rgba(245, 158, 11, 0.1f))
                    .boxShadow(offsetX = 0.px, offsetY = 30.px, blurRadius = 60.px, color = Color.rgba(0, 0, 0, 0.5f))
                    .styleModifier {
                        property("background-image", "radial-gradient(circle, rgba(245, 158, 11, 0.08) 1px, transparent 1px)")
                        property("background-size", "40px 40px")
                        property("overflow-x", "auto")
                    }
                    .padding(all = if (isDesktop) 80.px else 32.px),
                contentAlignment = Alignment.Center,
            ) {
                if (isDesktop) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        MatrixRootNode()

                        // Main Spine Connector (REDUCED FROM 60px to 20px)
                        Box(
                            modifier = Modifier
                                .width(20.px)
                                .height(4.px)
                                .backgroundColor(AMBER_500)
                                .boxShadow(offsetX = 0.px, offsetY = 0.px, blurRadius = 15.px, color = AMBER_500.toRgb().copy(alpha = 100)),
                        )

                        // Vertical Spine
                        Box(
                            modifier = Modifier
                                .width(4.px)
                                .height(700.px)
                                .backgroundColor(AMBER_500)
                                .boxShadow(offsetX = 0.px, offsetY = 0.px, blurRadius = 15.px, color = AMBER_500.toRgb().copy(alpha = 100))
                                .styleModifier {
                                    property("margin-left", "-2px")
                                },
                        )

                        Column(
                            modifier = Modifier.weight(1f).padding(left = 0.px),
                            verticalArrangement = Arrangement.spacedBy(80.px),
                        ) {
                            matrixData.forEachIndexed { index, category ->
                                MatrixCategoryBranch(
                                    title = category.title,
                                    skills = category.skills,
                                    accentColor = category.color,
                                    animate = animateSkills,
                                    delayMs = index * 300,
                                )
                            }
                        }
                    }
                } else {
                    Column(
                        modifier = Modifier.fillMaxWidth(),
                        verticalArrangement = Arrangement.spacedBy(48.px),
                    ) {
                        matrixData.forEach { category ->
                            Column {
                                H3(attrs = Modifier.color(category.color).margin(bottom = 20.px).toAttrs()) { Text(category.title.uppercase()) }
                                Row(
                                    modifier = Modifier.fillMaxWidth().styleModifier { property("flex-wrap", "wrap") },
                                    horizontalArrangement = Arrangement.Start,
                                ) {
                                    category.skills.forEach { skill ->
                                        Box(
                                            modifier = Modifier
                                                .fontSize(14.px)
                                                .color(Colors.White)
                                                .margin(all = 8.px)
                                                .padding(leftRight = 12.px, topBottom = 6.px)
                                                .backgroundColor(Color.rgba(255, 255, 255, 0.05f))
                                                .border(1.px, LineStyle.Solid, category.color.toRgb().copy(alpha = 100))
                                                .borderRadius(16.px),
                                        ) {
                                            Text(skill)
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
